import React, { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import {
  useParams, useLocation,
} from 'react-router-dom';

import Spinner from '../../../../../shared/_TBComponents/Spinner';
import CustomLoginFormContainer from './CustomLoginFormContainer';
import useScript from '../../../../../shared/hooks/useScript';
import { getCustomLoginData } from '../../../ducks/loginActions';
import BackgroundSlidesPageContainer from '../../shared/BackgroundSlidesPageContainer';
import PageWrapper from '../../shared/PageWrapper';
import useBackgroundSlides from '../../hooks/useBackgroundSlides';
import useAuthCheck from '../../hooks/useAuthCheck';

const SpinnerWrapper = styled('div')`
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const CustomLoginWrapper = () => {
  const { portalSlug } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  useAuthCheck();

  const customLoginInfo = useSelector((state) => state.auth.data.customLoginData);
  const customLoginDataError = useSelector((state) => state.auth.data.customLoginDataError);
  const isCustomLoginDataLoading = useSelector((state) => state.auth.ui.isCustomLoginDataLoading);

  /** Create slides array from custom login info
   * Currently using single slide, but can be extended for multiple slides
   */
  const slides = useMemo(() => {
    if (!customLoginInfo?.portalBackground) {
      return [];
    }
    return [{
      id: 0,
      url: customLoginInfo.portalBackground,
    }];
  }, [customLoginInfo?.portalBackground]);

  const { imageTransition } = useBackgroundSlides(slides, { autoPlay: slides.length > 1 });

  useScript('https://cdn.ywxi.net/js/1.js');

  useEffect(() => {
    /** get custom login data using portalSlug from the path
    (using the path info what custom login feature do we need to implement)
    We're using useLocation hook to monitor path changes.
    Listening for path changes with useRouteMatch didn't work. */
    dispatch(getCustomLoginData(portalSlug));
  }, [location]);

  useEffect(() => {
    /** If the custom login data fetch is rejected and we have an error,
     * we hard redirect to the main login page to avoid render bugs. */
    if (customLoginDataError !== null) {
      if (process.env.ENVIRONMENT === 'LOCAL') {
        window.location = `${window.location.protocol}/login`;
      } else {
        window.location = `https://${process.env.ENVIRONMENT === 'STAGING' ? 'staging.' : ''}app-v3.teambuildr.com/login`;
      }
    }
  }, [customLoginDataError]);

  const customGradientOverlay = 'linear-gradient(-20deg, rgba(68, 68, 68, 0.5) 0%, rgba(68, 68, 68, 0.0) 100%)';

  return (
    <BackgroundSlidesPageContainer
      backgroundColor={customLoginInfo?.primaryColor}
      gradientOverlay={customGradientOverlay}
      imageTransition={imageTransition}
    >
      <Helmet>
        <title>Sign In | TeamBuildr</title>
      </Helmet>
      {isCustomLoginDataLoading ? (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      ) : (
        <PageWrapper>
          <CustomLoginFormContainer />
        </PageWrapper>
      )}
    </BackgroundSlidesPageContainer>
  );
};

export default CustomLoginWrapper;
